import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Drawer,
	FormControlLabel,
	IconButton,
	Paper,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material'
import {
	Add,
	Close,
	Delete,
	Edit,
	FileUpload,
	Lock,
	Save,
	Cancel,
} from '@mui/icons-material'
import { doGet, doPost, doPut, doDelete } from '../../util/do-fetch'

// ---- Preset color swatches ----
const COLOR_PRESETS = [
	'#8884d8', '#82ca9d', '#ffc658', '#ff7300',
	'#0088fe', '#00C49F', '#e91e63', '#9c27b0',
]

// ---- MetricForm (Add / Edit) ----
const MetricForm = ({ initial, onSave, onCancel }) => {
	const [label,    setLabel]    = useState(initial?.label    || '')
	const [color,    setColor]    = useState(initial?.color    || COLOR_PRESETS[0])
	const [isActive, setIsActive] = useState(initial?.is_active !== false)
	const [saving,   setSaving]   = useState(false)
	const [error,    setError]    = useState(null)

	const previewKey = label
		? label.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').replace(/_+/g, '_').replace(/^_|_$/g, '')
		: ''

	const handleSave = async () => {
		if (!label.trim()) { setError('Label is required'); return }
		if (!color.trim()) { setError('Color is required'); return }
		setSaving(true)
		setError(null)
		try {
			await onSave({ label: label.trim(), color, is_active: isActive })
		}
		catch (e) {
			setError(e.message || 'Save failed')
			setSaving(false)
		}
	}

	return (
		<Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
			<Typography variant="subtitle2" sx={{ mb: 2 }}>
				{initial ? 'Edit Metric' : 'Add Metric'}
			</Typography>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

			<Stack spacing={2}>
				<TextField
					label="Label"
					value={label}
					onChange={e => setLabel(e.target.value)}
					size="small"
					fullWidth
					placeholder="e.g. YouTube Impressions"
				/>

				{previewKey && !initial && (
					<Typography variant="caption" color="text.secondary">
						Key: <strong>{previewKey}</strong>
					</Typography>
				)}

				<Box>
					<Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
						Color
					</Typography>
					<Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
						{COLOR_PRESETS.map(c => (
							<Box
								key={c}
								onClick={() => setColor(c)}
								sx={{
									width:  24,
									height: 24,
									borderRadius: '50%',
									backgroundColor: c,
									cursor: 'pointer',
									border: color === c ? '2px solid' : '2px solid transparent',
									borderColor: color === c ? 'text.primary' : 'transparent',
									flexShrink: 0,
								}}
							/>
						))}
						<TextField
							size="small"
							value={color}
							onChange={e => setColor(e.target.value)}
							sx={{ width: 110 }}
							inputProps={{ style: { fontFamily: 'monospace' } }}
						/>
						<Box sx={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: color, border: '1px solid', borderColor: 'divider' }} />
					</Box>
				</Box>

				<FormControlLabel
					control={<Switch checked={isActive} onChange={e => setIsActive(e.target.checked)} size="small" />}
					label="Active"
				/>

				<Box display="flex" gap={1} justifyContent="flex-end">
					<Button size="small" startIcon={<Cancel />} onClick={onCancel} disabled={saving}>Cancel</Button>
					<Button size="small" variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
						{saving ? <CircularProgress size={16} /> : 'Save'}
					</Button>
				</Box>
			</Stack>
		</Box>
	)
}

// ---- UploadDialog ----
const UploadDialog = ({ metric, onClose, onSuccess }) => {
	const fileInputRef = useRef(null)
	const [step,           setStep]           = useState('input')   // 'input' | 'column-select' | 'preview'
	const [rawText,        setRawText]         = useState('')
	const [parsing,        setParsing]         = useState(false)
	const [parseResult,    setParseResult]     = useState(null)     // { columns: [...] }
	const [selectedColumn, setSelectedColumn]  = useState(null)
	const [existingDates,  setExistingDates]   = useState({})       // { 'YYYY-MM-DD': value }
	const [importing,      setImporting]       = useState(false)
	const [parseError,     setParseError]      = useState(null)
	const [importError,    setImportError]     = useState(null)

	const handleFileChange = async (e) => {
		const file = e.target.files?.[0]
		if (!file) return
		const text = await file.text()
		setRawText(text)
	}

	const handleParse = async () => {
		if (!rawText.trim()) return
		setParsing(true)
		setParseError(null)
		try {
			const resp = await doPost('/admin/metrics/parse', { body: JSON.stringify({ rawText }) })
			const data = await resp.json()
			if (!resp.ok) throw new Error(data.errors?.[0] || 'Parse failed')
			setParseResult(data)

			// Also fetch existing dates for overwrite preview
			const existResp = await doGet(`/admin/metrics/${metric.metric_key}/existing-dates`)
			const existData = await existResp.json()
			setExistingDates(existData || {})

			if (data.columns.length === 1) {
				setSelectedColumn(data.columns[0])
				setStep('preview')
			}
			else {
				setStep('column-select')
			}
		}
		catch (e) {
			setParseError(e.message || 'Parse failed')
		}
		finally {
			setParsing(false)
		}
	}

	const handleSelectColumn = (col) => {
		setSelectedColumn(col)
		setStep('preview')
	}

	const handleImport = async () => {
		if (!selectedColumn) return
		setImporting(true)
		setImportError(null)
		try {
			const rows = selectedColumn.rows.filter(r => r.date && r.value != null)
			const resp = await doPost(`/admin/metrics/${metric.metric_key}/data`, { body: JSON.stringify({ rows }) })
			const data = await resp.json()
			if (!resp.ok) throw new Error(data.errors?.[0] || 'Import failed')
			onSuccess()
			onClose()
		}
		catch (e) {
			setImportError(e.message || 'Import failed')
		}
		finally {
			setImporting(false)
		}
	}

	const overwriteCount = selectedColumn
		? selectedColumn.rows.filter(r => r.date && existingDates[r.date] !== undefined).length
		: 0
	const validRowCount = selectedColumn
		? selectedColumn.rows.filter(r => r.date && r.value != null).length
		: 0
	const errorRowCount = selectedColumn
		? (selectedColumn.parse_errors?.length || 0)
		: 0

	return (
		<Dialog open onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				Upload Data — {metric.label}
			</DialogTitle>

			<DialogContent>
				{/* Step 1: Input */}
				{step === 'input' && (
					<Stack spacing={2} sx={{ mt: 1 }}>
						{parseError && <Alert severity="error">{parseError}</Alert>}
						<Typography variant="body2" color="text.secondary">
							Paste data from YouTube Analytics, Google Sheets, Excel, or any CSV/TSV export.
							You can also copy directly from a spreadsheet — tab-separated data pastes perfectly.
						</Typography>
						<TextField
							multiline
							rows={10}
							fullWidth
							placeholder="Paste your data here..."
							value={rawText}
							onChange={e => setRawText(e.target.value)}
							inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
						/>
						<Box display="flex" gap={1} alignItems="center">
							<input
								ref={fileInputRef}
								type="file"
								accept=".csv,.tsv,.txt"
								style={{ display: 'none' }}
								onChange={handleFileChange}
							/>
							<Button
								size="small"
								variant="outlined"
								startIcon={<FileUpload />}
								onClick={() => fileInputRef.current?.click()}
							>
								Upload File (.csv / .tsv)
							</Button>
							<Typography variant="caption" color="text.secondary">
								or paste above
							</Typography>
						</Box>
					</Stack>
				)}

				{/* Step 2: Column select */}
				{step === 'column-select' && parseResult && (
					<Stack spacing={2} sx={{ mt: 1 }}>
						<Typography variant="body2">
							Multiple numeric columns were found. Select the one you want to import:
						</Typography>
						<Stack spacing={1}>
							{parseResult.columns.map((col, i) => (
								<Paper
									key={i}
									variant="outlined"
									sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
									onClick={() => handleSelectColumn(col)}
								>
									<Typography variant="subtitle2">{col.name}</Typography>
									<Typography variant="caption" color="text.secondary">
										{col.rows.length} rows · first: {col.rows[0]?.date} · last: {col.rows[col.rows.length - 1]?.date}
									</Typography>
								</Paper>
							))}
						</Stack>
					</Stack>
				)}

				{/* Step 3: Preview */}
				{step === 'preview' && selectedColumn && (
					<Stack spacing={2} sx={{ mt: 1 }}>
						{importError && <Alert severity="error">{importError}</Alert>}

						<Box display="flex" gap={2} flexWrap="wrap">
							<Chip label={`${validRowCount} rows to import`} size="small" color="primary" variant="outlined" />
							{overwriteCount > 0 && (
								<Chip label={`${overwriteCount} will overwrite existing data`} size="small" color="warning" variant="outlined" />
							)}
							{errorRowCount > 0 && (
								<Chip label={`${errorRowCount} unparseable rows skipped`} size="small" color="error" variant="outlined" />
							)}
						</Box>

						<TableContainer component={Paper} sx={{ maxHeight: 360 }}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell><strong>Date</strong></TableCell>
										<TableCell align="right"><strong>New Value</strong></TableCell>
										<TableCell align="right"><strong>Existing Value</strong></TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{selectedColumn.rows.map((row, i) => {
										const existing = existingDates[row.date]
										const willOverwrite = existing !== undefined
										return (
											<TableRow
												key={i}
												sx={willOverwrite ? { backgroundColor: 'warning.light', opacity: 0.85 } : undefined}
											>
												<TableCell>{row.date}</TableCell>
												<TableCell align="right">{row.value?.toLocaleString()}</TableCell>
												<TableCell align="right" sx={{ color: willOverwrite ? 'warning.dark' : 'text.secondary' }}>
													{willOverwrite ? existing?.toLocaleString() : '—'}
												</TableCell>
											</TableRow>
										)
									})}
								</TableBody>
							</Table>
						</TableContainer>

						{selectedColumn.parse_errors?.length > 0 && (
							<Box>
								<Typography variant="caption" color="error">
									Skipped rows ({selectedColumn.parse_errors.length}):
								</Typography>
								{selectedColumn.parse_errors.map((e, i) => (
									<Typography key={i} variant="caption" display="block" color="text.secondary">
										Line {e.line}: {e.reason} — <em>{e.raw}</em>
									</Typography>
								))}
							</Box>
						)}
					</Stack>
				)}
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				{step === 'input' && (
					<Button variant="contained" onClick={handleParse} disabled={!rawText.trim() || parsing}>
						{parsing ? <CircularProgress size={16} /> : 'Parse'}
					</Button>
				)}
				{step === 'column-select' && (
					<Button onClick={() => setStep('input')}>Back</Button>
				)}
				{step === 'preview' && (
					<>
						<Button onClick={() => setStep(parseResult?.columns?.length > 1 ? 'column-select' : 'input')}>
							Back
						</Button>
						<Button variant="contained" onClick={handleImport} disabled={importing || validRowCount === 0}>
							{importing ? <CircularProgress size={16} /> : `Import ${validRowCount} rows`}
						</Button>
					</>
				)}
			</DialogActions>
		</Dialog>
	)
}

// ---- MetricsManager (main drawer) ----
const MetricsManager = ({ open, onClose, onMetricsChanged }) => {
	const theme = useTheme()
	const [metrics,       setMetrics]       = useState([])
	const [loading,       setLoading]       = useState(false)
	const [showAddForm,   setShowAddForm]   = useState(false)
	const [editingKey,    setEditingKey]    = useState(null)   // metric_key being edited
	const [uploadTarget,  setUploadTarget]  = useState(null)   // metric being uploaded to
	const [deleteConfirm, setDeleteConfirm] = useState(null)   // metric_key to delete
	const [deleteError,   setDeleteError]   = useState(null)

	const loadMetrics = useCallback(async () => {
		setLoading(true)
		try {
			const resp = await doGet('/admin/metrics')
			const data = await resp.json()
			setMetrics(data || [])
		}
		catch (e) {
			console.error('Failed to load metrics', e)
		}
		finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		if (open) loadMetrics()
	}, [open, loadMetrics])

	const handleToggleActive = async (metric) => {
		try {
			await doPut(`/admin/metrics/${metric.metric_key}`, {
				body: JSON.stringify({ label: metric.label, color: metric.color, is_active: !metric.is_active }),
			})
			await loadMetrics()
			onMetricsChanged()
		}
		catch (e) {
			console.error('Failed to toggle metric', e)
		}
	}

	const handleSaveNew = async (fields) => {
		const resp = await doPost('/admin/metrics', { body: JSON.stringify(fields) })
		const data = await resp.json()
		if (!resp.ok) throw new Error(data.errors?.[0] || 'Save failed')
		setShowAddForm(false)
		await loadMetrics()
		onMetricsChanged()
	}

	const handleSaveEdit = async (fields) => {
		const resp = await doPut(`/admin/metrics/${editingKey}`, { body: JSON.stringify(fields) })
		const data = await resp.json()
		if (!resp.ok) throw new Error(data.errors?.[0] || 'Save failed')
		setEditingKey(null)
		await loadMetrics()
		onMetricsChanged()
	}

	const handleDelete = async (metricKey) => {
		setDeleteError(null)
		try {
			const resp = await doDelete(`/admin/metrics/${metricKey}`)
			if (!resp.ok) throw new Error('Delete failed')
			setDeleteConfirm(null)
			await loadMetrics()
			onMetricsChanged()
		}
		catch (e) {
			setDeleteError(e.message || 'Delete failed')
		}
	}

	return (
		<>
			<Drawer
				anchor="right"
				open={open}
				onClose={onClose}
				PaperProps={{ sx: { width: 540 } }}
			>
				<Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
					{/* Header */}
					<Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
						<Typography variant="h6">Manage Metrics</Typography>
						<IconButton onClick={onClose} size="small"><Close /></IconButton>
					</Box>

					<Divider sx={{ mb: 2 }} />

					{/* Add form */}
					{showAddForm && (
						<MetricForm
							onSave={handleSaveNew}
							onCancel={() => setShowAddForm(false)}
						/>
					)}

					{/* Add button */}
					{!showAddForm && (
						<Button
							size="small"
							variant="outlined"
							startIcon={<Add />}
							onClick={() => { setShowAddForm(true); setEditingKey(null) }}
							sx={{ mb: 2, alignSelf: 'flex-start' }}
						>
							Add Metric
						</Button>
					)}

					{/* Metrics list */}
					{loading ? (
						<Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
							<CircularProgress size={24} />
						</Box>
					) : (
						<Box sx={{ flex: 1, overflowY: 'auto' }}>
							{/* System metric — locked, always first */}
							<Paper
								variant="outlined"
								sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1.5, opacity: 0.7 }}
							>
								<Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#4caf50', flexShrink: 0 }} />
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Typography variant="body2" noWrap>Purchase Total ($)</Typography>
									<Typography variant="caption" color="text.secondary">System metric — sourced from conversion data</Typography>
								</Box>
								<Tooltip title="System metric — cannot be modified">
									<Lock fontSize="small" sx={{ color: 'text.disabled' }} />
								</Tooltip>
							</Paper>

							{metrics.length === 0 && (
								<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
									No custom metrics yet. Add one above.
								</Typography>
							)}
							{metrics.map((metric) => (
								<Box key={metric.metric_key}>
									{/* Edit form inline */}
									{editingKey === metric.metric_key && (
										<MetricForm
											initial={metric}
											onSave={handleSaveEdit}
											onCancel={() => setEditingKey(null)}
										/>
									)}

									{/* Row */}
									{editingKey !== metric.metric_key && (
										<Paper
											variant="outlined"
											sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}
										>
											{/* Color swatch */}
											<Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: metric.color, flexShrink: 0 }} />

											{/* Label + metadata */}
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Typography variant="body2" noWrap>{metric.label}</Typography>
												<Typography variant="caption" color="text.secondary">
													{metric.data_point_count > 0
														? `${metric.data_point_count} pts · ${metric.min_date} → ${metric.max_date}`
														: 'No data yet'
													}
												</Typography>
											</Box>

											{/* Active toggle */}
											<Tooltip title={metric.is_active ? 'Active' : 'Inactive'}>
												<Switch
													size="small"
													checked={metric.is_active}
													onChange={() => handleToggleActive(metric)}
												/>
											</Tooltip>

											{/* Actions */}
											<Tooltip title="Upload data">
												<IconButton size="small" onClick={() => setUploadTarget(metric)}>
													<FileUpload fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip title="Edit">
												<IconButton size="small" onClick={() => { setEditingKey(metric.metric_key); setShowAddForm(false) }}>
													<Edit fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip title="Delete">
												<IconButton size="small" color="error" onClick={() => setDeleteConfirm(metric.metric_key)}>
													<Delete fontSize="small" />
												</IconButton>
											</Tooltip>
										</Paper>
									)}
								</Box>
							))}
						</Box>
					)}
				</Box>
			</Drawer>

			{/* Upload dialog */}
			{uploadTarget && (
				<UploadDialog
					metric={uploadTarget}
					onClose={() => setUploadTarget(null)}
					onSuccess={() => { loadMetrics(); onMetricsChanged() }}
				/>
			)}

			{/* Delete confirm dialog */}
			<Dialog open={Boolean(deleteConfirm)} onClose={() => { setDeleteConfirm(null); setDeleteError(null) }}>
				<DialogTitle>Delete metric?</DialogTitle>
				<DialogContent>
					{deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
					<Typography>
						This will permanently delete the metric and all of its data points. This cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => { setDeleteConfirm(null); setDeleteError(null) }}>Cancel</Button>
					<Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm)}>Delete</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default MetricsManager
